<script setup>
import { debounce } from "lodash-es";
import { ref, useTemplateRef, watch } from "vue";

import SvgIcon from "./SvgIcon.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Search..." },
  debounceMs: { type: Number, default: 300 },
});
const emit = defineEmits(["update:modelValue"]);

const localValue = ref(props.modelValue);
const inputRef = useTemplateRef("input");

watch(
  () => props.modelValue,
  (val) => {
    localValue.value = val;
  },
);

const debouncedEmit = debounce((val) => {
  emit("update:modelValue", val);
}, props.debounceMs);

function onInput(event) {
  localValue.value = event.target.value;
  debouncedEmit(localValue.value);
}

function clear() {
  localValue.value = "";
  debouncedEmit.cancel();
  emit("update:modelValue", "");
}

function focus() {
  inputRef.value.focus();
}

defineExpose({ focus });
</script>

<template>
  <div class="search">
    <SvgIcon name="system/search" width="16" height="16" class="icon" />
    <input
      ref="input"
      :value="localValue"
      type="text"
      :placeholder
      :aria-label="placeholder"
      class="input"
      v-bind="$attrs"
      @input="onInput"
    />
    <button v-if="localValue" class="clear" type="button" aria-label="Clear search" @click="clear">
      &times;
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.search {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.icon {
  position: absolute;
  left: 10px;
  width: 16px;
  height: 16px;
  color: p.$brand-70;
  pointer-events: none;
  transition: color 0.15s ease;
  z-index: 1;

  .search:focus-within & {
    color: c.$main;
  }
}

.input {
  width: 100%;
  min-width: 200px;
  height: 36px;
  padding: 0 2.25rem;
  font-family: inherit;
  font-size: 14px;
  color: c.$text;
  background-color: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: p.$brand-40;
  }

  &:hover:not(:focus) {
    border-color: c.$border;
  }

  &:focus {
    border-color: c.$main;
  }
}

.clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  color: p.$brand-70;
  font-size: 1.125rem;
  line-height: 1;

  &:hover {
    color: c.$main;
  }

  &:focus-visible {
    outline: 2px solid c.$main;
    outline-offset: 2px;
  }
}
</style>
